import React, { useState, useEffect } from 'react';
import { PageHeader } from '../components/PageHeader';
import { ChatScreen } from '../components/ChatScreen';
import { Row, Col } from '../components/Layout';
import { Message, User } from '../types';
import { messagesAPI, friendsAPI, getImageUrl } from '../services/api';
import { SafeImage } from '../components/SafeImage';
import { useAuth } from '../hooks/useAuth';
import { socketService } from '../services/socket';

const formatTime = (dateString?: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.max(0, now.getTime() - date.getTime());
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'Şimdi';
  if (minutes < 60) return `${minutes} dk önce`;
  if (hours < 24) return `${hours} saat önce`;
  if (days < 7) return `${days} gün önce`;
  return date.toLocaleDateString('tr-TR');
};

const MessagesPage: React.FC = () => {
  const [friends, setFriends] = useState<User[]>([]);
  const [selectedFriend, setSelectedFriend] = useState<User | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { user } = useAuth();

  useEffect(() => { loadFriends(); }, []);

  useEffect(() => {
    if (selectedFriend) {
      const targetId = (selectedFriend as any)._id || selectedFriend.id;
      loadMessages(targetId);
    }
  }, [selectedFriend]);

  useEffect(() => {
    const handleBackButton = (e: Event) => {
      if (selectedFriend) {
        e.preventDefault();
        setSelectedFriend(null);
      }
    };
    window.addEventListener('appBackButton', handleBackButton);
    return () => window.removeEventListener('appBackButton', handleBackButton);
  }, [selectedFriend]);

  useEffect(() => {
    if (selectedFriend) {
      const targetId = (selectedFriend as any)._id || selectedFriend.id;
      socketService.onNewMessage((message: Message) => {
        if ((message.sender as any)._id === targetId || (message.receiver as any)._id === targetId || message.sender.id === targetId || message.receiver.id === targetId) {
          setMessages(prev => {
            if (prev.some(m => m._id === message._id)) return prev;
            return [...prev, message];
          });
        }
      });
      return () => { socketService.removeListener('newMessage'); };
    }
  }, [selectedFriend]);

  const loadFriends = async () => {
    try {
      const data = await friendsAPI.getFriends();
      if (Array.isArray(data)) setFriends(data);
    } catch (e) { console.error(e); }
  };

  const loadMessages = async (friendId: string) => {
    try {
      setMessages(await messagesAPI.getMessages(friendId));
    } catch (e) { console.error(e); }
  };

  const sendMessage = async (content: string) => {
    if (!content.trim() || !selectedFriend) return;
    const targetId = (selectedFriend as any)._id || selectedFriend.id;
    try {
      await messagesAPI.sendMessage({ receiver: targetId, content });
      const tempMsg: Message = {
        _id: Date.now().toString(), content,
        sender: { id: user?.id || '', username: user?.username || '', profileImage: user?.profileImage || '' } as User,
        receiver: selectedFriend, timestamp: new Date(), read: false
      };
      setMessages(prev => [...prev, tempMsg]);
    } catch (e) { console.error(e); }
  };

  const filtered = friends.filter(f => f.username.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      {!selectedFriend && (
        <div style={{ padding: '0 20px', flex: 1, overflowY: 'auto' }}>
          <PageHeader title="Mesajlar" />

          {/* Search */}
          <div style={{ background: 'var(--surface)', borderRadius: 16, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <span style={{ fontSize: 18, color: 'var(--muted)' }}>🔍</span>
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Mesajlarda ara..."
              style={{ flex: 1, background: 'transparent', border: 'none', color: '#fff', fontSize: 15, outline: 'none' }}
            />
          </div>

          {/* Online Friends (Stories style) */}
          <div style={{ padding: '0 18px 20px' }}>
            <div className="syne" style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Çevrimiçi Arkadaşların</div>
            <Row style={{ gap: 14, overflowX: 'auto', paddingBottom: 20, margin: '0 -18px', padding: '0 18px 20px' }} className="hide-scrollbar">
              {friends.filter(f => f.isLive && f.username.toLowerCase().includes(searchQuery.toLowerCase())).map((f, i) => (
                <Col key={i} onClick={() => setSelectedFriend(f)} style={{ alignItems: 'center', gap: 6, cursor: 'pointer', flexShrink: 0 }}>
                  <div style={{ width: 62, height: 62, borderRadius: 20, padding: 2, background: 'linear-gradient(135deg,var(--coral),var(--purple))' }}>
                    <div style={{ width: '100%', height: '100%', borderRadius: 18, border: '2px solid var(--bg)', overflow: 'hidden' }}>
                      <SafeImage src={f.profileImage ? getImageUrl(f.profileImage) : undefined} fallbackSrc={`https://ui-avatars.com/api/?name=${f.username}&background=random`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 500, fontFamily: 'DM Sans' }}>{f.username.split(' ')[0]}</div>
                </Col>
              ))}
              {friends.filter(f => f.isLive && f.username.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && <span style={{ fontSize: 12, color: 'var(--muted)' }}>Aktif arkadaşın yok</span>}
            </Row>

            {/* List */}
            <div className="syne" style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Tüm Konuşmalar</div>
            <Col style={{ gap: 4 }}>
              {filtered.map((f, i) => (
                <Row key={i} onClick={() => setSelectedFriend(f)} style={{ gap: 14, padding: '12px', background: 'var(--surface)', borderRadius: 20, cursor: 'pointer' }}>
                  <div style={{ position: 'relative' }}>
                    <div style={{ width: 54, height: 54, borderRadius: 18, overflow: 'hidden' }}>
                      <SafeImage src={f.profileImage ? getImageUrl(f.profileImage) : undefined} fallbackSrc={`https://ui-avatars.com/api/?name=${f.username}&background=random`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                    {f.isLive && <div style={{ position: 'absolute', bottom: -2, right: -2, width: 14, height: 14, background: 'var(--green)', borderRadius: '50%', border: '3px solid var(--surface)' }} />}
                  </div>
                  <Col style={{ flex: 1, justifyContent: 'center' }}>
                    <Row style={{ justifyContent: 'space-between', marginBottom: 4 }}>
                      <div className="syne" style={{ fontSize: 15, fontWeight: 700 }}>{f.username}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{formatTime((f as any).lastMessageTime) || 'Şimdi'}</div>
                    </Row>
                    <div style={{ fontSize: 13, color: 'var(--text)', opacity: 0.8 }}>{(f as any).lastMessage || 'Sohbet başlatmak için tıkla'}</div>
                  </Col>
                </Row>
              ))}
            </Col>
          </div>
        </div>
      )}

      {selectedFriend && (
        <ChatScreen
          friend={selectedFriend}
          onBack={() => setSelectedFriend(null)}
          messages={messages}
          currentUserId={user?.id || ''}
          onSendMessage={sendMessage}
        />
      )}
    </div>
  );
};

export default MessagesPage;
